import Modal from '@components/V4/Modal/Modal';
import { useGetAllCoursesLite, useGetStudentChurnScore, useGetUserProfile } from '@actions';
import ChurnRiskBadge from '@components/V4/ChurnRisk/ChurnRiskBadge';
import { GetAllCoursesLiteDTO } from '@shared/clientTypes/DTOs/CoursesDTO';
import { UserProfileDTO } from '@shared/clientTypes/DTOs/UserDTO';
import { createContext } from 'react';
import StudentCommentsSection from './StudentCommentsSection/StudentCommentsSection';
import StudentDetailsSection from './StudentDetailsSection/StudentDetailsSection';
import StudentTabsSection, { StudentTab } from './StudentTabsSection/StudentTabsSection';
import DetailsSectionPills from '../DetailsSectionPills';
import { getUserPills } from '@modules/Admin/utils/helpers';
import Button from '@components/V4/Button';
import { ReactComponent as BackSignIcon } from '@assets/icons/back-sign.svg';

interface StudentDetailsPageProps {
  studentId: string;
  tab?: StudentTab;
  isBackButtonActive?: boolean;
  onGoBack?: () => void;
  onEnrollmentModalOpen: (id: string) => void;
  onSessionModalOpen: (id: string) => void;
  onMentorModalOpen: (id: string) => void;
}

interface StudentDetailsPageContext {
  student?: Partial<UserProfileDTO>;
  courses?: GetAllCoursesLiteDTO[];
  onEnrollmentModalOpen: (id: string) => void;
  onSessionModalOpen: (id: string) => void;
  onMentorModalOpen: (id: string) => void;
}

export const StudentDetailsPageContext = createContext<StudentDetailsPageContext>({
  onEnrollmentModalOpen: () => console.warn('onEnrollmentModalOpen not implemented'),
  onSessionModalOpen: () => console.warn('onSessionModalOpen not implemented'),
  onMentorModalOpen: () => console.warn('onMentorModalOpen not implemented'),
});

const StudentDetailsPage = (props: StudentDetailsPageProps) => {
  const {
    studentId,
    tab,
    isBackButtonActive = false,
    onGoBack,
    onEnrollmentModalOpen,
    onSessionModalOpen,
    onMentorModalOpen,
  } = props;
  const { data: student } = useGetUserProfile(studentId);
  const { data: courses } = useGetAllCoursesLite();
  const { data: churn } = useGetStudentChurnScore(studentId);

  return (
    <>
      <Modal.Header className="relative font-raleway text-xl font-bold text-center mb-6">
        {isBackButtonActive && (
          <Button
            icon={<BackSignIcon />}
            className="absolute top-0 left-6"
            variant="secondary"
            onClick={onGoBack}
            buttonText="Back"
          />
        )}

        <h3>Student Profile</h3>
        <h3>{student?.fullName}</h3>
        <DetailsSectionPills pills={getUserPills(student)} />
        <div className="mt-3 flex items-center justify-center gap-2 text-sm font-normal">
          <span className="text-customGrey">Churn risk:</span>
          <ChurnRiskBadge
            score={churn?.score}
            band={churn?.band}
            size="large"
            emptyText="Not scored"
            suffix={
              typeof churn?.delta30 === 'number' && churn.delta30 !== 0 ? (
                <span className="font-normal opacity-80">
                  · {churn.delta30 > 0 ? '+' : ''}
                  {Math.round(churn.delta30)} in 30d
                </span>
              ) : null
            }
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <StudentDetailsPageContext.Provider
          value={{
            student,
            courses,
            onEnrollmentModalOpen,
            onSessionModalOpen,
            onMentorModalOpen,
          }}
        >
          {student && <StudentDetailsSection />}
          {student && <StudentCommentsSection />}
          <StudentTabsSection tab={tab} />
        </StudentDetailsPageContext.Provider>
      </Modal.Body>
    </>
  );
};

export default StudentDetailsPage;
